import { useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Tile } from '@rossum/components/tile/Tile';
import { Stack, TextField } from '@rossum/ui/material';
import { formatBsonType } from '../../types';
import { FormSchema } from '../types';
import { dataTypes, useColumnHeaderData } from './useColumnHeaderData';

const ColumnContent = ({
  attributeName,
  type,
  bsonType,
  control,
}: {
  attributeName: string;
  type: string;
  bsonType: string | string[] | null | undefined;
  control: Control<FormSchema>;
}) => {
  const intl = useIntl();
  const formattedBsonType = formatBsonType(bsonType);
  return (
    <Stack p={3} alignItems="flex-end">
      <Stack spacing={3} width="50%">
        <Controller
          control={control}
          name={`metadata.columnNames.${attributeName}`}
          render={({ field: { value, ...fieldProps } }) => (
            <TextField
              {...fieldProps}
              value={value}
              size="small"
              label={intl.formatMessage({
                id: 'features.datasets.settings.form.columnsSection.name.label',
              })}
              fullWidth
            />
          )}
        />

        <TextField
          value={attributeName}
          size="small"
          label={intl.formatMessage({
            id: 'features.datasets.settings.form.columnsSection.datasetAttributeName.label',
          })}
          disabled
          fullWidth
        />

        <TextField
          value={dataTypes(intl)[type]?.label ?? type}
          size="small"
          label={intl.formatMessage({
            id: 'features.datasets.settings.form.columnsSection.type.label',
          })}
          // Surface the raw inferred bsonType when it fails to map to an
          // editable type (e.g. unions like `int | string`), as a debugging aid.
          helperText={type === 'unknown' ? formattedBsonType : undefined}
          disabled
          fullWidth
        />
      </Stack>
    </Stack>
  );
};

export const ColumnDefinition = ({
  attributeName,
  type,
  bsonType,
  control,
}: {
  attributeName: string;
  type: string;
  bsonType: string | string[] | null | undefined;
  control: Control<FormSchema>;
}) => {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);

  const { icon, title, description } = useColumnHeaderData({
    control,
    attributeName,
    type,
    intl,
  });

  return (
    <Tile
      iconProps={{ content: icon, color: 'secondary' }}
      title={title}
      description={description}
      quickActions={[]}
      expandable={{
        expanded,
        onToggleExpanded: () => setExpanded(prev => !prev),
        content: (
          <ColumnContent
            attributeName={attributeName}
            type={type}
            bsonType={bsonType}
            control={control}
          />
        ),
      }}
      dragHandle={null}
      data-id={attributeName}
      data-cy="dataset-column-definition"
    />
  );
};
